import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ZapIcon,
  CreditCardIcon,
  AwardIcon,
  CalendarIcon,
  BarChart3Icon,
  BookOpenIcon,
  TargetIcon,
  CheckCircleIcon,
  Gamepad2Icon,
  Percent,
  UsersIcon,
  TrophyIcon,
} from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import learningLevelsData from '../../../data/learningLevels.json';
import { getDefaultAcademicYearRange, getSeasonYearFromDate } from '../../../utils/dashboardDateRange';

const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString());
const pct = (p) => (p == null || Number.isNaN(Number(p)) ? '—' : `${Number(p).toFixed(1)}%`);

/** نسبة نجاح مرجحة لكل الألعاب، أو من الحقل إن وُجد في الـ API */
function computeGamesOverallSuccessPercent(gm) {
  if (!gm) return null;
  const direct = gm.overallSuccessRatePercent ?? gm.overallSuccessPercent;
  if (direct != null && !Number.isNaN(Number(direct))) {
    return Number(direct);
  }
  const list = gm.byGame;
  if (!Array.isArray(list) || list.length === 0) return null;
  let weighted = 0;
  let sessions = 0;
  for (const row of list) {
    const ps = Number(row.playSessions) || 0;
    const sr = Number(row.successRatePercent);
    if (ps <= 0 || Number.isNaN(sr)) continue;
    weighted += ps * sr;
    sessions += ps;
  }
  if (sessions <= 0) return null;
  return weighted / sessions;
}

const QUALITY_TONES = {
  rose: {
    bar: 'bg-gradient-to-r from-rose-400 to-rose-600 shadow-sm shadow-rose-500/30',
    track: 'bg-rose-100/90 ring-1 ring-rose-200/60',
    pill: 'bg-rose-100 text-rose-900 ring-1 ring-rose-200/80',
    value: 'text-rose-800'
  },
  amber: {
    bar: 'bg-gradient-to-r from-amber-400 to-orange-500 shadow-sm shadow-amber-500/25',
    track: 'bg-amber-100/90 ring-1 ring-amber-200/60',
    pill: 'bg-amber-100 text-amber-950 ring-1 ring-amber-200/80',
    value: 'text-amber-900'
  },
  emerald: {
    bar: 'bg-gradient-to-r from-emerald-400 to-teal-600 shadow-sm shadow-emerald-500/25',
    track: 'bg-emerald-100/90 ring-1 ring-emerald-200/60',
    pill: 'bg-emerald-100 text-emerald-950 ring-1 ring-emerald-200/80',
    value: 'text-emerald-900'
  }
};

function QualityBar({ label, count, percent, tone }) {
  const q = QUALITY_TONES[tone] || QUALITY_TONES.emerald;
  const w = Math.min(100, Math.max(0, Number(percent) || 0));
  return (
    <div className="rounded-xl border border-slate-200/60 bg-white/80 p-3 shadow-sm ring-1 ring-slate-900/5 backdrop-blur-sm">
      <div className="flex justify-between gap-3 text-xs">
        <span className={`inline-flex max-w-[65%] items-center rounded-lg px-2 py-0.5 font-semibold ${q.pill}`}>{label}</span>
        <span className={`shrink-0 font-bold tabular-nums ${q.value}`}>
          {fmt(count)} · {pct(percent)}
        </span>
      </div>
      <div className={`mt-2.5 h-2.5 overflow-hidden rounded-full ${q.track}`}>
        <div
          className={`h-full rounded-full transition-all duration-500 ease-out ${q.bar}`}
          style={{ width: `${w}%` }}
        />
      </div>
    </div>
  );
}

const METRIC_VARIANTS = {
  emerald: {
    card: 'border-emerald-200/80 bg-gradient-to-br from-emerald-50 via-white to-teal-50/70 shadow-emerald-500/10 ring-emerald-500/10',
    icon: 'bg-emerald-500/15 text-emerald-700 ring-emerald-400/30',
    label: 'text-emerald-900/75',
    value: 'text-emerald-950',
    sub: 'text-emerald-800/85',
    glow: 'from-emerald-400/25'
  },
  lime: {
    card: 'border-lime-200/80 bg-gradient-to-br from-lime-50 via-white to-emerald-50/60 shadow-lime-500/10 ring-lime-500/10',
    icon: 'bg-lime-500/15 text-lime-800 ring-lime-400/30',
    label: 'text-lime-900/75',
    value: 'text-lime-950',
    sub: 'text-lime-900/80',
    glow: 'from-lime-400/25'
  },
  blue: {
    card: 'border-blue-200/80 bg-gradient-to-br from-blue-50 via-white to-sky-50/70 shadow-blue-500/10 ring-blue-500/10',
    icon: 'bg-blue-500/15 text-blue-700 ring-blue-400/30',
    label: 'text-blue-900/75',
    value: 'text-blue-950',
    sub: 'text-blue-900/80',
    glow: 'from-blue-400/25'
  },
  cyan: {
    card: 'border-cyan-200/80 bg-gradient-to-br from-cyan-50 via-white to-sky-50/60 shadow-cyan-500/10 ring-cyan-500/10',
    icon: 'bg-cyan-500/15 text-cyan-800 ring-cyan-400/30',
    label: 'text-cyan-950/80',
    value: 'text-cyan-950',
    sub: 'text-cyan-900/80',
    glow: 'from-cyan-400/25'
  },
  sky: {
    card: 'border-sky-200/80 bg-gradient-to-br from-sky-50 via-white to-blue-50/50 shadow-sky-500/10 ring-sky-500/10',
    icon: 'bg-sky-500/15 text-sky-800 ring-sky-400/30',
    label: 'text-sky-950/80',
    value: 'text-sky-950',
    sub: 'text-sky-900/80',
    glow: 'from-sky-400/25'
  },
  indigo: {
    card: 'border-indigo-200/80 bg-gradient-to-br from-indigo-50 via-white to-violet-50/50 shadow-indigo-500/10 ring-indigo-500/10',
    icon: 'bg-indigo-500/15 text-indigo-700 ring-indigo-400/30',
    label: 'text-indigo-950/80',
    value: 'text-indigo-950',
    sub: 'text-indigo-900/80',
    glow: 'from-indigo-400/25'
  },
  violet: {
    card: 'border-violet-200/80 bg-gradient-to-br from-violet-50 via-white to-purple-50/50 shadow-violet-500/10 ring-violet-500/10',
    icon: 'bg-violet-500/15 text-violet-700 ring-violet-400/30',
    label: 'text-violet-950/80',
    value: 'text-violet-950',
    sub: 'text-violet-900/80',
    glow: 'from-violet-400/25'
  },
  purple: {
    card: 'border-purple-200/80 bg-gradient-to-br from-purple-50 via-white to-fuchsia-50/40 shadow-purple-500/10 ring-purple-500/10',
    icon: 'bg-purple-500/15 text-purple-800 ring-purple-400/30',
    label: 'text-purple-950/80',
    value: 'text-purple-950',
    sub: 'text-purple-900/80',
    glow: 'from-purple-400/25'
  },
  fuchsia: {
    card: 'border-fuchsia-200/80 bg-gradient-to-br from-fuchsia-50 via-white to-pink-50/40 shadow-fuchsia-500/10 ring-fuchsia-500/10',
    icon: 'bg-fuchsia-500/15 text-fuchsia-800 ring-fuchsia-400/30',
    label: 'text-fuchsia-950/80',
    value: 'text-fuchsia-950',
    sub: 'text-fuchsia-900/80',
    glow: 'from-fuchsia-400/25'
  },
  amber: {
    card: 'border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-orange-50/50 shadow-amber-500/10 ring-amber-500/10',
    icon: 'bg-amber-500/15 text-amber-800 ring-amber-400/30',
    label: 'text-amber-950/80',
    value: 'text-amber-950',
    sub: 'text-amber-900/80',
    glow: 'from-amber-400/25'
  },
  rose: {
    card: 'border-rose-200/80 bg-gradient-to-br from-rose-50 via-white to-orange-50/40 shadow-rose-500/10 ring-rose-500/10',
    icon: 'bg-rose-500/15 text-rose-800 ring-rose-400/30',
    label: 'text-rose-950/80',
    value: 'text-rose-950',
    sub: 'text-rose-900/80',
    glow: 'from-rose-400/25'
  },
  slate: {
    card: 'border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-slate-100/50 shadow-slate-500/10 ring-slate-500/10',
    icon: 'bg-slate-500/15 text-slate-700 ring-slate-400/30',
    label: 'text-slate-700',
    value: 'text-slate-900',
    sub: 'text-slate-600',
    glow: 'from-slate-400/20'
  }
};

function MetricTile({ icon: Icon, label, value, sub, variant = 'slate' }) {
  const v = METRIC_VARIANTS[variant] || METRIC_VARIANTS.slate;
  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border p-5 shadow-md ring-1 transition duration-300 hover:-translate-y-0.5 hover:shadow-lg ${v.card}`}
    >
      <div
        className={`pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${v.glow} to-transparent opacity-80`}
      />
      <div className="relative">
        {Icon && (
          <span
            className={`mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl ring-1 ${v.icon}`}
          >
            <Icon className="h-5 w-5" />
          </span>
        )}
        <p className={`text-[11px] font-bold uppercase tracking-wide ${v.label}`}>{label}</p>
        <p className={`mt-1.5 text-3xl font-bold tabular-nums tracking-tight ${v.value}`}>{value}</p>
        {sub && <p className={`mt-1 text-xs font-semibold ${v.sub}`}>{sub}</p>}
      </div>
    </div>
  );
}

const OPERATION_KEYS = ['addition', 'subtraction', 'mixed', 'multiplication', 'division'];
const DIFFICULTY_KEYS = ['easy', 'medium', 'hard'];

const StudentStatistics = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { studentId } = useParams();
  const defaultRange = useMemo(() => getDefaultAcademicYearRange(), []);
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);
  const [loading, setLoading] = useState(true);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [student, setStudent] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [flashAnzanData, setFlashAnzanData] = useState(null);
  const [flashCardsData, setFlashCardsData] = useState(null);
  const [gamesData, setGamesData] = useState(null);
  const [challengesData, setChallengesData] = useState(null);
  const [competitionsData, setCompetitionsData] = useState(null);

  const translateLevelName = (levelName) => {
    if (!levelName) return '';
    const translations = learningLevelsData[levelName] || {};
    return translations[currentLanguage] || levelName;
  };

  const fetchRangeDashboardStats = useCallback(
    async (from, to) => {
      if (!studentId) return;
      const params = {
        dateFrom: from,
        dateTo: to,
        year: getSeasonYearFromDate(from),
        studentId
      };
      try {
        const [dailyRes, faRes, fcRes, gamesRes, competitionsRes, challengesRes] = await Promise.all([
          api.get('/education/student-dashboard/daily-exercises', { params }),
          api.get('/education/student-dashboard/flash-anzan', { params }),
          api.get('/education/student-dashboard/flash-cards', { params }),
          api.get('/education/student-dashboard/games', { params }),
          api.get('/education/student-dashboard/competitions', { params }),
          api.get('/education/student-dashboard/challenges', { params })
        ]);
        if (dailyRes.data?.success) {
          setDailyData(dailyRes.data.data);
        } else {
          setDailyData(null);
        }
        if (faRes.data?.success) {
          setFlashAnzanData(faRes.data.data);
        } else {
          setFlashAnzanData(null);
        }
        if (fcRes.data?.success) {
          setFlashCardsData(fcRes.data.data);
        } else {
          setFlashCardsData(null);
        }
        if (gamesRes.data?.success) {
          setGamesData(gamesRes.data.data);
        } else {
          setGamesData(null);
        }
        if (competitionsRes.data?.success) {
          setCompetitionsData(competitionsRes.data.data);
        } else {
          setCompetitionsData(null);
        }
        if (challengesRes.data?.success) {
          setChallengesData(challengesRes.data.data);
        } else {
          setChallengesData(null);
        }
      } catch (e) {
        console.error(e);
        setDailyData(null);
        setFlashAnzanData(null);
        setFlashCardsData(null);
        setGamesData(null);
        setCompetitionsData(null);
        setChallengesData(null);
        toast.error(t('users.students.stats.loadDailyError'));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-fetching entire page when only locale changes
    [studentId]
  );

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    const r = getDefaultAcademicYearRange();
    setDateFrom(r.dateFrom);
    setDateTo(r.dateTo);

    (async () => {
      setLoading(true);
      try {
        const studentRes = await api.get(`/students/user/${studentId}`);
        if (cancelled) return;
        if (studentRes.data.success && studentRes.data.data?.student) {
          setStudent(studentRes.data.data.student);
        } else {
          setStudent(null);
        }

        await fetchRangeDashboardStats(r.dateFrom, r.dateTo);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching student statistics:', err);
          toast.error(t('users.students.stats.loadError'));
          setStudent(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t intentionally omitted to avoid full reload on locale change
  }, [studentId, fetchRangeDashboardStats]);

  const handleApplyRange = async () => {
    if (!studentId) return;
    setRangeLoading(true);
    try {
      await fetchRangeDashboardStats(dateFrom, dateTo);
    } catch (err) {
      console.error(err);
      setDailyData(null);
      setFlashAnzanData(null);
      setFlashCardsData(null);
      setGamesData(null);
      setCompetitionsData(null);
      setChallengesData(null);
      toast.error(t('users.students.stats.loadDailyError'));
    } finally {
      setRangeLoading(false);
    }
  };

  const handleResetRange = async () => {
    const r = getDefaultAcademicYearRange();
    setDateFrom(r.dateFrom);
    setDateTo(r.dateTo);
    setRangeLoading(true);
    try {
      await fetchRangeDashboardStats(r.dateFrom, r.dateTo);
    } catch (err) {
      console.error(err);
      setDailyData(null);
      setFlashAnzanData(null);
      setFlashCardsData(null);
      setGamesData(null);
      setCompetitionsData(null);
      setChallengesData(null);
      toast.error(t('users.students.stats.loadDailyError'));
    } finally {
      setRangeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{t('users.students.studentNotFoundDesc')}</p>
          <button
            onClick={() => navigate('/dashboard/students')}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  const dm = dailyData?.metrics;
  const period = dailyData?.period;
  const buckets = dm?.attemptQualityBuckets;
  const periodLabel =
    period?.seasonYearLabel ||
    (period?.start && period?.end ? `${period.start} → ${period.end}` : null);

  const fam = flashAnzanData?.metrics;
  const faPeriod = flashAnzanData?.period;
  const flashAnzanPeriodLabel =
    faPeriod?.seasonYearLabel ||
    (faPeriod?.start && faPeriod?.end ? `${faPeriod.start} → ${faPeriod.end}` : null);
  const aq = fam?.answerQuality;

  const fcm = flashCardsData?.metrics;
  const fcPeriod = flashCardsData?.period;
  const flashCardsPeriodLabel =
    fcPeriod?.seasonYearLabel ||
    (fcPeriod?.start && fcPeriod?.end ? `${fcPeriod.start} → ${fcPeriod.end}` : null);
  const sq = fcm?.sessionQualityBuckets;

  const gm = gamesData?.metrics;
  const gamesPeriod = gamesData?.period;
  const gamesPeriodLabel =
    gamesPeriod?.seasonYearLabel ||
    (gamesPeriod?.start && gamesPeriod?.end ? `${gamesPeriod.start} → ${gamesPeriod.end}` : null);
  const gamesOverallSuccessPercent = computeGamesOverallSuccessPercent(gm);

  const chm = challengesData?.metrics;
  const challengesPeriod = challengesData?.period;
  const challengesPeriodLabel =
    challengesPeriod?.seasonYearLabel ||
    (challengesPeriod?.start && challengesPeriod?.end
      ? `${challengesPeriod.start} → ${challengesPeriod.end}`
      : null);

  const compm = competitionsData?.metrics;
  const competitionsPeriod = competitionsData?.period;
  const competitionsPeriodLabel =
    competitionsPeriod?.seasonYearLabel ||
    (competitionsPeriod?.start && competitionsPeriod?.end
      ? `${competitionsPeriod.start} → ${competitionsPeriod.end}`
      : null);
  const compQ = compm?.participationQualityBuckets;

  const studentName = student
    ? `${student.first_name || ''} ${student.last_name || ''}`.trim() || t('users.students.student')
    : t('users.students.student');
  const levelName = student?.level_name ? translateLevelName(student.level_name) || student.level_name : null;

  return (
    <div className="w-full space-y-8" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard/students')}
              className="rounded-xl border border-slate-200/80 bg-white p-2.5 shadow-md shadow-slate-900/5 ring-1 ring-slate-900/5 transition hover:border-indigo-200 hover:bg-indigo-50/50 hover:shadow-indigo-500/10"
            >
              <ArrowLeftIcon className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-indigo-900 to-violet-900 bg-clip-text text-transparent">
                {studentName}
              </h1>
              {levelName && (
                <p className="mt-1.5">
                  <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-100 px-2.5 py-1 text-sm font-semibold text-amber-900 ring-1 ring-amber-300/60">
                    <AwardIcon className="h-4 w-4 shrink-0 text-amber-600" />
                    {levelName}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-indigo-200/50 bg-gradient-to-br from-white via-indigo-50/30 to-violet-50/40 p-4 shadow-lg shadow-indigo-500/5 ring-1 ring-indigo-500/10 backdrop-blur-sm">
          <p className="text-xs font-medium text-indigo-900/70 mb-3">{t('users.students.stats.dateFilterScope')}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:flex-wrap">
            <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
              <span className="flex items-center gap-1.5 text-indigo-800/80">
                <CalendarIcon className="h-3.5 w-3.5 text-indigo-500" />
                {t('dashboard.stats.dateFrom')}
              </span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-inner shadow-slate-900/5 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
              <span className="flex items-center gap-1.5 text-indigo-800/80">
                <CalendarIcon className="h-3.5 w-3.5 text-indigo-500" />
                {t('dashboard.stats.dateTo')}
              </span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-inner shadow-slate-900/5 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
              />
            </label>
            <button
              type="button"
              onClick={handleResetRange}
              disabled={rangeLoading}
              className="rounded-xl border border-slate-300/80 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-400/80 disabled:opacity-60"
            >
              {t('dashboard.stats.resetDateRange')}
            </button>
            <button
              type="button"
              onClick={handleApplyRange}
              disabled={rangeLoading}
              className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 hover:from-indigo-500 hover:to-violet-500 disabled:opacity-60"
            >
              {rangeLoading ? t('common.loading') : t('dashboard.stats.applyRange')}
            </button>
          </div>
        </div>

        <section className="w-full overflow-hidden rounded-3xl border border-emerald-200/40 bg-white shadow-xl shadow-emerald-500/10 ring-1 ring-emerald-500/10">
          <div className="flex flex-col gap-2 border-b border-emerald-200/30 px-6 py-4 sm:flex-row sm:items-center sm:justify-between bg-gradient-to-r from-emerald-500/10 via-teal-50/80 to-lime-50/60">
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/30 ring-2 ring-white/50">
                <BookOpenIcon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-emerald-950 truncate tracking-tight">
                  {t('users.students.stats.dailyExercisesPeriod')}
                </h2>
                {periodLabel && (
                  <p className="text-xs font-semibold text-emerald-800/70 truncate">
                    {periodLabel}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="p-6 relative bg-gradient-to-b from-white to-emerald-50/20">
            {rangeLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-3xl bg-white/70 backdrop-blur-[2px]">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
              </div>
            )}
            {dm ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <MetricTile
                    icon={BarChart3Icon}
                    label={t('dashboard.stats.totalSessions')}
                    value={fmt(dm.totalSessions)}
                    variant="emerald"
                  />
                  <MetricTile
                    icon={TargetIcon}
                    label={t('dashboard.stats.totalAnswers')}
                    value={fmt(dm.totalAnswers)}
                    variant="lime"
                  />
                </div>
                {buckets && (
                  <div className="mt-8 space-y-3">
                    <h3 className="inline-flex rounded-lg bg-emerald-100/80 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-900 ring-1 ring-emerald-300/50">
                      {t('dashboard.stats.attemptQuality')}
                    </h3>
                    <QualityBar
                      label={t('users.students.stats.qualityWeakUnder50')}
                      count={buckets.weakUnder50Percent?.count}
                      percent={buckets.weakUnder50Percent?.percentOfItems}
                      tone="rose"
                    />
                    <QualityBar
                      label={t('users.students.stats.qualityMedium50to69')}
                      count={buckets.medium50To69Percent?.count}
                      percent={buckets.medium50To69Percent?.percentOfItems}
                      tone="amber"
                    />
                    <QualityBar
                      label={t('users.students.stats.qualityGood70Plus')}
                      count={buckets.good70PlusPercent?.count}
                      percent={buckets.good70PlusPercent?.percentOfItems}
                      tone="emerald"
                    />
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">{t('users.students.stats.noData')}</p>
            )}
          </div>
        </section>

        <section className="w-full overflow-hidden rounded-3xl border border-sky-200/50 bg-white shadow-xl shadow-sky-500/10 ring-1 ring-sky-400/15">
          <div className="flex flex-col gap-2 border-b border-sky-200/40 px-6 py-4 sm:flex-row sm:items-center sm:justify-between bg-gradient-to-r from-sky-500/15 via-blue-50/70 to-indigo-50/50">
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-lg shadow-sky-500/35 ring-2 ring-white/50">
                <ZapIcon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-sky-950 truncate tracking-tight">
                  {t('dashboard.stats.flashAnzanStats')}
                </h2>
                {flashAnzanPeriodLabel && (
                  <p className="text-xs font-semibold text-sky-800/75 truncate">{flashAnzanPeriodLabel}</p>
                )}
              </div>
            </div>
          </div>
          <div className="p-6 relative bg-gradient-to-b from-white to-sky-50/25">
            {rangeLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-3xl bg-white/70 backdrop-blur-[2px]">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
              </div>
            )}
            {fam ? (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <MetricTile
                      icon={BarChart3Icon}
                      label={t('dashboard.stats.totalSessions')}
                      value={fmt(fam.totalSessions)}
                      variant="blue"
                    />
                    <MetricTile
                      icon={CheckCircleIcon}
                      label={t('dashboard.stats.completedSessions')}
                      value={fmt(fam.completedSessions)}
                      sub={t('dashboard.stats.percentOfTotalSessions', {
                        p: pct(fam.completedPercentOfTotal)
                      })}
                      variant="cyan"
                    />
                  </div>

                  {fam.byOperation && (
                    <div className="mt-6 overflow-hidden rounded-xl border border-sky-200/60 bg-white shadow-md ring-1 ring-sky-500/10">
                      <table className="w-full min-w-[260px] table-fixed text-xs">
                        <thead>
                          <tr className="bg-gradient-to-r from-sky-600 to-blue-700 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
                            <th className="py-2.5 ps-3 pe-2 text-start">
                              {t('dashboard.stats.operation')}
                            </th>
                            <th className="py-2.5 px-1 text-end text-white/95">
                              {t('dashboard.stats.totalSessions')}
                            </th>
                            <th className="py-2.5 px-1 text-end text-white/95">
                              {t('dashboard.stats.completedSessions')}
                            </th>
                            <th className="py-2.5 pe-3 ps-1 text-end text-white">
                              {t('dashboard.stats.correctCount')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-sky-100 bg-sky-50/30">
                          {OPERATION_KEYS.map((op, idx) => {
                            const row = fam.byOperation[op];
                            if (!row) return null;
                            return (
                              <tr
                                key={op}
                                className={`transition-colors hover:bg-sky-100/80 ${idx % 2 === 0 ? 'bg-white/80' : 'bg-sky-50/50'}`}
                              >
                                <td className="py-2 ps-3 pe-2 font-semibold text-sky-950 leading-tight">
                                  {t(`dashboard.stats.operations.${op}`)}
                                </td>
                                <td className="py-2 px-1.5 text-end tabular-nums font-medium text-slate-800">
                                  {fmt(row.sessions)}
                                </td>
                                <td className="py-2 px-1.5 text-end tabular-nums font-medium text-slate-800">
                                  {fmt(row.completed)}
                                </td>
                                <td className="py-2 pe-3 ps-1.5 text-end tabular-nums font-bold text-emerald-700">
                                  {fmt(row.correct)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {aq && (
                    <div className="mt-8 space-y-3">
                      <h3 className="inline-flex rounded-lg bg-sky-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-sky-950 ring-1 ring-sky-300/50">
                        {t('dashboard.stats.answerQualityCompleted')}
                      </h3>
                      <QualityBar
                        label={t('users.students.stats.qualityWeakUnder50')}
                        count={aq.weakUnder50Percent?.count}
                        percent={aq.weakUnder50Percent?.percentOfCompleted}
                        tone="rose"
                      />
                      <QualityBar
                        label={t('users.students.stats.qualityMedium50to69')}
                        count={aq.medium50To69Percent?.count}
                        percent={aq.medium50To69Percent?.percentOfCompleted}
                        tone="amber"
                      />
                      <QualityBar
                        label={t('users.students.stats.qualityGood70Plus')}
                        count={aq.good70PlusPercent?.count}
                        percent={aq.good70PlusPercent?.percentOfCompleted}
                        tone="emerald"
                      />
                    </div>
                  )}

                  {fam.byDifficulty && (
                    <div className="mt-8 grid gap-4 sm:grid-cols-3">
                      {DIFFICULTY_KEYS.map((d) => {
                        const row = fam.byDifficulty[d];
                        if (!row) return null;
                        const dv = d === 'easy' ? 'emerald' : d === 'medium' ? 'amber' : 'rose';
                        return (
                          <MetricTile
                            key={d}
                            label={t(`dashboard.stats.difficulty.${d}`)}
                            value={fmt(row.sessions)}
                            sub={t('dashboard.stats.successRate', { p: pct(row.successRatePercent) })}
                            variant={dv}
                          />
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500">{t('users.students.stats.noData')}</p>
              )}
          </div>
        </section>

        <section className="w-full overflow-hidden rounded-3xl border border-violet-200/50 bg-white shadow-xl shadow-violet-500/10 ring-1 ring-violet-400/15">
          <div className="flex flex-col gap-2 border-b border-violet-200/40 px-6 py-4 sm:flex-row sm:items-center sm:justify-between bg-gradient-to-r from-violet-500/12 via-indigo-50/80 to-fuchsia-50/50">
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-lg shadow-violet-500/35 ring-2 ring-white/50">
                <CreditCardIcon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-violet-950 truncate tracking-tight">
                  {t('dashboard.stats.flashCardsStats')}
                </h2>
                {flashCardsPeriodLabel && (
                  <p className="text-xs font-semibold text-violet-800/75 truncate">{flashCardsPeriodLabel}</p>
                )}
              </div>
            </div>
          </div>
          <div className="p-6 relative bg-gradient-to-b from-white to-violet-50/20">
            {rangeLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-3xl bg-white/70 backdrop-blur-[2px]">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
              </div>
            )}
            {fcm ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <MetricTile
                    icon={AwardIcon}
                    label={t('users.students.stats.flashCardLevelShort')}
                    value={fmt(fcm.studentLevelNumber)}
                    variant="indigo"
                  />
                  <MetricTile
                    icon={BarChart3Icon}
                    label={t('dashboard.stats.totalSessions')}
                    value={fmt(fcm.totalSessions)}
                    variant="violet"
                  />
                  <MetricTile
                    icon={CheckCircleIcon}
                    label={t('dashboard.stats.completedSessions')}
                    value={fmt(fcm.completedSessions)}
                    sub={t('dashboard.stats.percentOfTotalSessions', {
                      p: pct(fcm.completedPercentOfTotal)
                    })}
                    variant="purple"
                  />
                  <MetricTile
                    icon={TargetIcon}
                    label={t('users.students.stats.flashCardsPrimaryScope')}
                    value={fmt(fcm.primaryScopeTotalSessions)}
                    variant="fuchsia"
                  />
                </div>

                {fcm.byFlashCardType && Object.keys(fcm.byFlashCardType).length > 0 && (
                  <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {Object.entries(fcm.byFlashCardType)
                      .sort(([a], [b]) => Number(a) - Number(b))
                      .map(([id, row], i) => {
                        const cycle = ['indigo', 'violet', 'purple', 'fuchsia'];
                        return (
                          <MetricTile
                            key={id}
                            icon={CreditCardIcon}
                            label={t('dashboard.stats.flashCardTypeN', { n: id })}
                            value={fmt(row.sessions)}
                            sub={t('dashboard.stats.avgSuccess', { p: pct(row.averageSuccessPercent) })}
                            variant={cycle[i % cycle.length]}
                          />
                        );
                      })}
                  </div>
                )}

                {sq && (
                  <div className="mt-8 space-y-3">
                    <h3 className="inline-flex rounded-lg bg-violet-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-violet-950 ring-1 ring-violet-300/50">
                      {t('dashboard.stats.answerQualitySessions')}
                    </h3>
                    <QualityBar
                      label={t('users.students.stats.qualityWeakUnder50')}
                      count={sq.weakUnder50Percent?.count}
                      percent={sq.weakUnder50Percent?.percentOfSessions}
                      tone="rose"
                    />
                    <QualityBar
                      label={t('users.students.stats.qualityMedium50to69')}
                      count={sq.medium50To69Percent?.count}
                      percent={sq.medium50To69Percent?.percentOfSessions}
                      tone="amber"
                    />
                    <QualityBar
                      label={t('users.students.stats.qualityGood70Plus')}
                      count={sq.good70PlusPercent?.count}
                      percent={sq.good70PlusPercent?.percentOfSessions}
                      tone="emerald"
                    />
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">{t('users.students.stats.noData')}</p>
            )}
          </div>
        </section>

        <section className="w-full overflow-hidden rounded-3xl border border-amber-200/60 bg-white shadow-xl shadow-amber-500/10 ring-1 ring-amber-400/15">
          <div className="flex flex-col gap-2 border-b border-amber-200/40 px-6 py-4 sm:flex-row sm:items-center sm:justify-between bg-gradient-to-r from-amber-500/15 via-orange-50/80 to-yellow-50/50">
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/35 ring-2 ring-white/50">
                <Gamepad2Icon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-amber-950 truncate tracking-tight">
                  {t('dashboard.stats.sectionGames')}
                </h2>
                {gamesPeriodLabel && (
                  <p className="text-xs font-semibold text-amber-900/75 truncate">{gamesPeriodLabel}</p>
                )}
              </div>
            </div>
          </div>
          <div className="relative bg-gradient-to-b from-white to-amber-50/25 p-6">
            {rangeLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-3xl bg-white/70 backdrop-blur-[2px]">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
              </div>
            )}
            {gm ? (
              <>
                <div className="grid w-full gap-4 sm:grid-cols-2">
                  <MetricTile
                    icon={Gamepad2Icon}
                    label={t('dashboard.stats.totalPlaySessions')}
                    value={fmt(gm.totalPlaySessions)}
                    variant="amber"
                  />
                  <MetricTile
                    icon={Percent}
                    label={t('dashboard.stats.overallSuccessRate')}
                    value={gamesOverallSuccessPercent != null ? pct(gamesOverallSuccessPercent) : '—'}
                    variant="emerald"
                  />
                </div>
                {Array.isArray(gm.byGame) && gm.byGame.length > 0 && (
                  <div className="mt-8">
                    <h3 className="mb-3 inline-flex rounded-lg bg-amber-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-950 ring-1 ring-amber-300/50">
                      {t('dashboard.stats.perGameBreakdown')}
                    </h3>
                    <div className="mt-4 overflow-hidden rounded-xl border border-amber-200/70 bg-white shadow-md ring-1 ring-amber-500/10">
                      <table className="w-full min-w-[280px] text-sm" dir="ltr">
                        <thead>
                          <tr className="bg-gradient-to-r from-amber-600 to-orange-600 text-[11px] font-bold uppercase tracking-wide text-white">
                            <th className="py-2.5 ps-4 pe-2 text-left">{t('dashboard.stats.gameIdCol')}</th>
                            <th className="py-2.5 px-2 text-end">{t('dashboard.stats.playSessionsCol')}</th>
                            <th className="py-2.5 pe-4 ps-2 text-end">{t('dashboard.stats.successRateCol')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-amber-100">
                          {gm.byGame.map((row, idx) => (
                            <tr
                              key={row.gameId ?? idx}
                              className={`transition-colors hover:bg-amber-50/90 ${idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/40'}`}
                            >
                              <td className="py-2.5 ps-4 pe-2 text-left font-semibold text-amber-950">
                                {row.gameName || `—`}
                              </td>
                              <td className="py-2.5 px-2 text-end tabular-nums font-medium text-slate-800">
                                {fmt(row.playSessions)}
                              </td>
                              <td className="py-2.5 pe-4 ps-2 text-end tabular-nums font-bold text-emerald-700">
                                {pct(row.successRatePercent)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">{t('users.students.stats.noData')}</p>
            )}
          </div>
        </section>

        <section className="w-full overflow-hidden rounded-3xl border border-cyan-200/60 bg-white shadow-xl shadow-cyan-500/10 ring-1 ring-cyan-400/15">
          <div className="flex flex-col gap-2 border-b border-cyan-200/40 px-6 py-4 sm:flex-row sm:items-center sm:justify-between bg-gradient-to-r from-cyan-500/15 via-teal-50/80 to-sky-50/50">
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-600 text-white shadow-lg shadow-cyan-500/35 ring-2 ring-white/50">
                <TrophyIcon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-cyan-950 truncate tracking-tight">
                  {t('dashboard.stats.sectionCompetitions')}
                </h2>
                {competitionsPeriodLabel && (
                  <p className="text-xs font-semibold text-cyan-900/75 truncate">{competitionsPeriodLabel}</p>
                )}
              </div>
            </div>
          </div>
          <div className="relative bg-gradient-to-b from-white to-cyan-50/25 p-6">
            {rangeLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-3xl bg-white/70 backdrop-blur-[2px]">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent" />
              </div>
            )}
            {compm ? (
              <>
                <div className="grid w-full gap-4 sm:grid-cols-2">
                  <MetricTile
                    icon={TrophyIcon}
                    label={t('dashboard.stats.competitionsParticipatedInPeriod')}
                    value={fmt(compm.competitionsParticipatedInPeriod)}
                    variant="cyan"
                  />
                  <MetricTile
                    icon={Percent}
                    label={t('dashboard.stats.overallSuccessRate')}
                    value={pct(compm.overallSuccessRatePercent)}
                    variant="emerald"
                  />
                </div>
                {compQ && (
                  <div className="mt-8 space-y-3">
                    <h3 className="inline-flex rounded-lg bg-cyan-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-cyan-950 ring-1 ring-cyan-300/50">
                      {t('dashboard.stats.competitionParticipationQuality')}
                    </h3>
                    <QualityBar
                      label={t('users.students.stats.qualityWeakUnder50')}
                      count={compQ.weakUnder50Percent?.count}
                      percent={compQ.weakUnder50Percent?.percentOfCompetitions}
                      tone="rose"
                    />
                    <QualityBar
                      label={t('users.students.stats.qualityMedium50to69')}
                      count={compQ.medium50To69Percent?.count}
                      percent={compQ.medium50To69Percent?.percentOfCompetitions}
                      tone="amber"
                    />
                    <QualityBar
                      label={t('users.students.stats.qualityGood70Plus')}
                      count={compQ.good70PlusPercent?.count}
                      percent={compQ.good70PlusPercent?.percentOfCompetitions}
                      tone="emerald"
                    />
                  </div>
                )}
                {Array.isArray(compm.byCompetition) && compm.byCompetition.length > 0 && (
                  <div className="mt-8">
                    <h3 className="mb-3 inline-flex rounded-lg bg-cyan-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-cyan-950 ring-1 ring-cyan-300/50">
                      {t('dashboard.stats.perCompetitionTitle')}
                    </h3>
                    <div className="mt-4 overflow-hidden rounded-xl border border-cyan-200/70 bg-white shadow-md ring-1 ring-cyan-500/10">
                      <table className="w-full min-w-[280px] text-sm" dir="ltr">
                        <thead>
                          <tr className="bg-gradient-to-r from-cyan-600 to-teal-600 text-[11px] font-bold uppercase tracking-wide text-white">
                            <th className="py-2.5 ps-4 pe-2 text-left">{t('dashboard.stats.competitionNameCol')}</th>
                            <th className="py-2.5 px-2 text-end">{t('dashboard.stats.roundsCol')}</th>
                            <th className="py-2.5 pe-4 ps-2 text-end">{t('dashboard.stats.successRateCol')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-cyan-100">
                          {compm.byCompetition.map((row, idx) => (
                            <tr
                              key={row.competitionId ?? idx}
                              className={`transition-colors hover:bg-cyan-50/90 ${idx % 2 === 0 ? 'bg-white' : 'bg-cyan-50/40'}`}
                            >
                              <td className="py-2.5 ps-4 pe-2 text-left font-semibold text-cyan-950">
                                {row.name ?? row.competitionId ?? '—'}
                              </td>
                              <td className="py-2.5 px-2 text-end tabular-nums font-medium text-slate-800">
                                {fmt(row.rounds)}
                              </td>
                              <td className="py-2.5 pe-4 ps-2 text-end tabular-nums font-bold text-emerald-700">
                                {pct(row.successRatePercent)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">{t('users.students.stats.noData')}</p>
            )}
          </div>
        </section>

        <section className="w-full overflow-hidden rounded-3xl border border-rose-200/60 bg-white shadow-xl shadow-rose-500/10 ring-1 ring-rose-400/15">
          <div className="flex flex-col gap-2 border-b border-rose-200/40 px-6 py-4 sm:flex-row sm:items-center sm:justify-between bg-gradient-to-r from-rose-500/15 via-red-50/80 to-orange-50/40">
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-lg shadow-rose-500/35 ring-2 ring-white/50">
                <TargetIcon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-rose-950 truncate tracking-tight">
                  {t('dashboard.stats.sectionChallenges')}
                </h2>
                {challengesPeriodLabel && (
                  <p className="text-xs font-semibold text-rose-900/75 truncate">{challengesPeriodLabel}</p>
                )}
              </div>
            </div>
          </div>
          <div className="relative bg-gradient-to-b from-white to-rose-50/25 p-6">
            {rangeLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-3xl bg-white/70 backdrop-blur-[2px]">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-rose-500 border-t-transparent" />
              </div>
            )}
            {chm ? (
              <>
                <div className="grid w-full gap-4 sm:grid-cols-2">
                  <MetricTile
                    icon={UsersIcon}
                    label={t('dashboard.stats.totalParticipations')}
                    value={fmt(chm.totalParticipations)}
                    variant="rose"
                  />
                  <MetricTile
                    icon={Percent}
                    label={t('dashboard.stats.overallSuccessRate')}
                    value={pct(chm.overallSuccessRatePercent)}
                    variant="emerald"
                  />
                </div>
                {Array.isArray(chm.byChallenge) && chm.byChallenge.length > 0 && (
                  <div className="mt-8">
                    <h3 className="mb-3 inline-flex rounded-lg bg-rose-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-rose-950 ring-1 ring-rose-300/50">
                      {t('dashboard.stats.perChallengeTitle')}
                    </h3>
                    <div className="mt-4 overflow-hidden rounded-xl border border-rose-200/70 bg-white shadow-md ring-1 ring-rose-500/10">
                      <table className="w-full min-w-[280px] text-sm" dir="ltr">
                        <thead>
                          <tr className="bg-gradient-to-r from-rose-600 to-red-600 text-[11px] font-bold uppercase tracking-wide text-white">
                            <th className="py-2.5 ps-4 pe-2 text-left">{t('dashboard.stats.challengeNameCol')}</th>
                            <th className="py-2.5 px-2 text-end">{t('dashboard.stats.submissionsCol')}</th>
                            <th className="py-2.5 pe-4 ps-2 text-end">{t('dashboard.stats.successRateCol')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-rose-100">
                          {chm.byChallenge.map((row, idx) => (
                            <tr
                              key={row.challengeId ?? idx}
                              className={`transition-colors hover:bg-rose-50/90 ${idx % 2 === 0 ? 'bg-white' : 'bg-rose-50/40'}`}
                            >
                              <td className="py-2.5 ps-4 pe-2 text-left font-semibold text-rose-950">
                                {row.name ?? row.challengeId ?? '—'}
                              </td>
                              <td className="py-2.5 px-2 text-end tabular-nums font-medium text-slate-800">
                                {fmt(row.submissions)}
                              </td>
                              <td className="py-2.5 pe-4 ps-2 text-end tabular-nums font-bold text-emerald-700">
                                {pct(row.successRatePercent)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">{t('users.students.stats.noData')}</p>
            )}
          </div>
        </section>
    </div>
  );
};

export default StudentStatistics;
